import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatCompactNumber } from '@/lib/format'
import styles from './KpiCard.module.css'

export interface KpiBreakdown {
  label: string
  value: number
  tone?: 'success' | 'danger' | 'warning' | 'neutral'
}

interface KpiCardProps {
  label: string
  value: number
  icon: LucideIcon
  /** The two or three numbers that explain the headline figure. */
  breakdown?: KpiBreakdown[]
  to?: string
  /** Highlights the card that needs action — the approval queue. */
  emphasis?: boolean
  hint?: string
}

export function KpiCard({ label, value, icon: Icon, breakdown, to, emphasis = false, hint }: KpiCardProps) {
  const content = (
    <>
      <div className={styles.top}>
        <span className={styles.label}>{label}</span>
        <span className={styles.icon}>
          <Icon aria-hidden="true" />
        </span>
      </div>

      <p className={styles.value} title={String(value)}>
        {formatCompactNumber(value)}
      </p>

      {breakdown && breakdown.length > 0 ? (
        <ul className={styles.breakdown} role="list">
          {breakdown.map((entry) => (
            <li key={entry.label} className={cn(styles.breakdownItem, styles[entry.tone ?? 'neutral'])}>
              <span className={styles.breakdownDot} aria-hidden="true" />
              <span className={styles.breakdownValue}>{formatCompactNumber(entry.value)}</span>
              <span className={styles.breakdownLabel}>{entry.label}</span>
            </li>
          ))}
        </ul>
      ) : hint ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}
    </>
  )

  if (to) {
    return (
      <Link to={to} className={cn(styles.card, styles.linked, emphasis && styles.emphasis)}>
        {content}
      </Link>
    )
  }

  return <div className={cn(styles.card, emphasis && styles.emphasis)}>{content}</div>
}
